import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { MdOutlineCancel } from "react-icons/md";
import { FaSearch, FaTimes, FaChevronDown, FaSignOutAlt, FaUserShield, FaCrown } from "react-icons/fa";
import axios from "axios";
import { useBaseUrl } from "../Contexts/BaseUrlContext";
import { links as originalLinks, canOpen, expandAccess } from "../Data/menu";
import { useStateContext } from "../Contexts/ContextProvider";
import PanelSwitcher from "../WebsiteAdmin/PanelSwitcher";

const CSS = `
.sb-root { background: radial-gradient(120% 60% at 0% 0%, rgba(56,189,248,.14), transparent 60%), radial-gradient(90% 50% at 100% 100%, rgba(168,85,247,.14), transparent 60%), linear-gradient(180deg, #0b1220 0%, #0f172a 55%, #111827 100%); }
.sb-scroll { scrollbar-width: thin; scrollbar-color: rgba(148,163,184,.25) transparent; }
.sb-scroll::-webkit-scrollbar { width: 6px; }
.sb-scroll::-webkit-scrollbar-thumb { background: rgba(148,163,184,.25); border-radius: 999px; }
.sb-scroll::-webkit-scrollbar-track { background: transparent; }
.sb-item { outline: none; transition: background-color .18s ease, color .18s ease, transform .15s ease, box-shadow .2s ease; }
.sb-item:hover { transform: translateX(3px); }
.sb-item:active { transform: scale(.98); }
.sb-item:focus-visible { box-shadow: 0 0 0 2px rgba(255,255,255,.6); }
.sb-sec { display: grid; grid-template-rows: 1fr; transition: grid-template-rows .28s cubic-bezier(.2,.8,.2,1); }
.sb-sec.sb-closed { grid-template-rows: 0fr; }
.sb-sec > div { overflow: hidden; }
@keyframes sbPop { 0% { transform: scale(.6); opacity: 0; } 100% { transform: scale(1); opacity: 1; } }
.sb-badge { animation: sbPop .3s cubic-bezier(.2,.8,.2,1) both; }
@keyframes sbGlow { 0%,100% { opacity: .55; } 50% { opacity: 1; } }
.sb-glow { animation: sbGlow 2.4s ease-in-out infinite; }
@keyframes sbToast { from { opacity: 0; transform: translate(-50%, -8px); } to { opacity: 1; transform: translate(-50%, 0); } }
.sb-toast { animation: sbToast .25s ease both; }
@media (prefers-reduced-motion: reduce) {
  .sb-sec, .sb-item { transition: none; }
  .sb-item:hover { transform: none; }
  .sb-badge, .sb-toast, .sb-glow { animation: none; }
}
`;

// Colour for each menu group (icons and the small dot next to the group name)
const ACCENTS = {
  "": { dot: "bg-sky-400", icon: "text-sky-300" },
  "App Users": { dot: "bg-cyan-400", icon: "text-cyan-300" },
  "Owner Links": { dot: "bg-teal-400", icon: "text-teal-300" },
  Videos: { dot: "bg-rose-400", icon: "text-rose-300" },
  Groups: { dot: "bg-violet-400", icon: "text-violet-300" },
  "Ads Reward": { dot: "bg-indigo-400", icon: "text-indigo-300" },
  Customer: { dot: "bg-amber-400", icon: "text-amber-300" },
  Monetization: { dot: "bg-pink-400", icon: "text-pink-300" },
  "App Updates": { dot: "bg-blue-400", icon: "text-blue-300" },
  "System Users": { dot: "bg-slate-300", icon: "text-slate-300" },
};

const COLLAPSE_KEY = "ycnp_sidebar_closed";

// Where each "needs action" badge number comes from
const BADGE_SOURCES = {
  report_request: { file: "users.php", action: "reported-users", params: { reported: "0" }, keys: ["reports", "data"] },
  admin_request: { file: "users.php", action: "admin-request", keys: ["data"] },
  user_message_report: { file: "group_report.php", action: "getUserMessageReports", keys: ["reports"] },
  group_message_report: { file: "group_report.php", action: "getGroupMessageReports", keys: ["reports"] },
  group_joining_req: { file: "group.php", action: "groupJoiningRequestList", keys: ["requests"] },
  withdrawal_request: { file: "wallet.php", action: "withdrawRequestList", params: { status: "pending" }, keys: ["withdrawals"] },
  customer_request: { file: "customer.php", action: "customerRequestList", keys: ["requests"] },
};

const toArray = (d, keys) => {
  if (Array.isArray(d)) return d;
  if (!d || typeof d !== "object") return [];
  for (const k of keys) if (Array.isArray(d[k])) return d[k];
  return [];
};

const Sidebar = () => {
  const { baseUrl } = useBaseUrl();
  const navigation = useNavigate();
  const location = useLocation();
  const { activeMenu, setActiveMenu, screenSize, currentColor } = useStateContext();

  const [filteredLinks, setFilteredLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isOwner, setIsOwner] = useState(false);
  const [access, setAccess] = useState([]);
  const [me, setMe] = useState({ name: "", id: "" });
  const [denied, setDenied] = useState("");
  const [query, setQuery] = useState("");
  const [closed, setClosed] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(COLLAPSE_KEY) || "{}");
    } catch (e) {
      return {};
    }
  });
  const [badges, setBadges] = useState({});
  const searchRef = useRef(null);

  /* ----- who is logged in, and what can they open ----- */

  useEffect(() => {
    const fetchUserAccess = async () => {
      try {
        const res = await axios.post(`${baseUrl}/auth.php`, { action: "auth-check" }, { headers: { "Content-Type": "multipart/form-data" }, withCredentials: true });
        const data = res.data;
        if (data.access === false) {
          navigation("/login");
          return;
        }
        setMe({ name: data.name || "", id: data.id || "" });
        if (String(data.id) !== "1" && data?.access) {
          const accessList = expandAccess(data.access_list || []);
          const newLinks = originalLinks
            .map((section) => {
              const filtered = section.links.filter((link) => {
                if (link.key === "dashboard") return true;
                if (link.ownerOnly) return false;
                return accessList.includes(link.key);
              });
              return filtered.length > 0 ? { ...section, links: filtered } : null;
            })
            .filter(Boolean);
          setAccess(accessList);
          setIsOwner(false);
          setFilteredLinks(newLinks);
        } else {
          setIsOwner(true);
          setFilteredLinks(originalLinks);
        }
      } catch (error) {
        console.error("Error fetching user access:", error);
        navigation("/login");
        setFilteredLinks([]);
      } finally {
        setLoading(false);
      }
    };
    fetchUserAccess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ----- page protection for managers ----- */

  useEffect(() => {
    if (loading || isOwner) return;
    if (!canOpen(location.pathname, access)) {
      setDenied("You do not have permission to open that page.");
      navigation("/dashboard", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, loading, isOwner, access]);

  useEffect(() => {
    if (!denied) return undefined;
    const t = setTimeout(() => setDenied(""), 4000);
    return () => clearTimeout(t);
  }, [denied]);

  /* ----- live "waiting" counts next to menu items ----- */

  const visibleKeys = useMemo(() => new Set(filteredLinks.flatMap((s) => s.links.map((l) => l.key))), [filteredLinks]);

  const loadBadges = useCallback(async () => {
    const jobs = Object.entries(BADGE_SOURCES).filter(([key]) => visibleKeys.has(key));
    const results = await Promise.allSettled(
      jobs.map(([, src]) => {
        const fd = new FormData();
        fd.append("action", src.action);
        Object.entries(src.params || {}).forEach(([k, v]) => fd.append(k, v));
        return fetch(`${baseUrl}/${src.file}`, { method: "POST", body: fd, credentials: "include" }).then((r) => r.json());
      })
    );
    const next = {};
    results.forEach((r, i) => {
      if (r.status === "fulfilled") next[jobs[i][0]] = toArray(r.value, jobs[i][1].keys).length;
    });
    setBadges(next);
  }, [baseUrl, visibleKeys]);

  useEffect(() => {
    if (loading || !visibleKeys.size) return undefined;
    loadBadges();
    const t = setInterval(loadBadges, 60000);
    return () => clearInterval(t);
  }, [loading, visibleKeys, loadBadges]);

  // refresh counts after moving to another page (an action may have changed them)
  useEffect(() => {
    if (!loading && visibleKeys.size) {
      const t = setTimeout(loadBadges, 1500);
      return () => clearTimeout(t);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  /* ----- sections, search, keyboard ----- */

  const toggleSection = (title) =>
    setClosed((c) => {
      const next = { ...c, [title]: !c[title] };
      try {
        localStorage.setItem(COLLAPSE_KEY, JSON.stringify(next));
      } catch (e) {
        /* ignore */
      }
      return next;
    });

  const isActivePath = (name) => location.pathname === `/${name}` || (name === "dashboard" && location.pathname === "/");

  const q = query.trim().toLowerCase();
  const shownSections = useMemo(() => {
    if (!q) return filteredLinks;
    return filteredLinks
      .map((s) => ({ ...s, links: s.links.filter((l) => l.title.toLowerCase().includes(q) || String(s.title || "").toLowerCase().includes(q)) }))
      .filter((s) => s.links.length);
  }, [filteredLinks, q]);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        if (!activeMenu) setActiveMenu(true);
        setTimeout(() => searchRef.current?.focus(), 50);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [activeMenu, setActiveMenu]);

  const handleCloseSidebar = () => {
    setQuery("");
    if (activeMenu && screenSize <= 900) setActiveMenu(false);
  };

  const logout = async () => {
    try {
      const fd = new FormData();
      fd.append("action", "logout");
      await fetch(`${baseUrl}/auth.php`, { method: "POST", body: fd, credentials: "include" });
    } catch (e) {
      /* ignore */
    }
    navigation("/login");
  };

  const totalWaiting = Object.values(badges).reduce((s, n) => s + n, 0);
  const initials =
    String(me.name || "A")
      .trim()
      .split(/\s+/)
      .map((w) => w[0])
      .slice(0, 2)
      .join("")
      .toUpperCase() || "A";

  if (loading) {
    return (
      <div className="sb-root h-screen p-4 space-y-3">
        <style>{CSS}</style>
        <div className="h-14 rounded-2xl bg-white/10 animate-pulse" />
        <div className="h-10 rounded-xl bg-white/5 animate-pulse" />
        {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
          <div key={i} className="h-10 rounded-xl bg-white/5 animate-pulse" />
        ))}
      </div>
    );
  }

  if (!activeMenu) {
    return denied ? (
      <div role="alert" className="sb-toast fixed top-4 left-1/2 px-4 py-3 rounded-xl bg-rose-600 text-white text-sm font-semibold shadow-xl" style={{ zIndex: 2000, transform: "translateX(-50%)" }}>
        🔒 {denied}
      </div>
    ) : null;
  }

  return (
    <div className="sb-root h-screen flex flex-col text-slate-300">
      <style>{CSS}</style>

      {denied && (
        <div role="alert" className="sb-toast fixed top-4 left-1/2 px-4 py-3 rounded-xl bg-rose-600 text-white text-sm font-semibold shadow-xl" style={{ zIndex: 2000, transform: "translateX(-50%)" }}>
          🔒 {denied}
        </div>
      )}

      {/* ===== Brand ===== */}
      <div className="shrink-0 px-4 pt-4">
        <div className="flex items-center gap-3">
          <Link to="/dashboard" onClick={handleCloseSidebar} className="flex-1 flex items-center gap-3 rounded-2xl bg-white px-3 py-2 shadow-lg shadow-black/20" style={{ outline: "none" }}>
            <img src="/ycnp.png" alt="YCNP" style={{ height: 34 }} />
            <span className="ml-auto text-[9px] font-black uppercase tracking-[.18em] text-slate-400 leading-tight text-right">
              Admin
              <br />
              Panel
            </span>
          </Link>
          <button type="button" onClick={() => setActiveMenu(false)} className="md:hidden w-9 h-9 rounded-xl bg-white/10 text-white flex items-center justify-center" aria-label="Close menu">
            <MdOutlineCancel />
          </button>
        </div>

        {/* App | Website switch (owner only) */}
        {isOwner && <PanelSwitcher />}

        {/* Search */}
        <div className="relative mt-4">
          <FaSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" size={11} />
          <input
            ref={searchRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") setQuery("");
              if (e.key === "Enter" && shownSections[0]?.links[0]) {
                navigation(`/${shownSections[0].links[0].name}`);
                handleCloseSidebar();
              }
            }}
            placeholder="Search pages..."
            aria-label="Search pages"
            className="w-full h-10 pl-9 pr-16 rounded-xl bg-white/5 border border-white/10 focus:border-white/30 focus:bg-white/10 text-sm text-white placeholder:text-slate-500 focus:outline-none transition-colors"
          />
          {query ? (
            <button type="button" onClick={() => setQuery("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-white" aria-label="Clear">
              <FaTimes size={11} />
            </button>
          ) : (
            <kbd className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[10px] font-semibold text-slate-400 border border-white/15 rounded-md px-1.5 py-0.5">Ctrl K</kbd>
          )}
        </div>

        {/* Waiting summary */}
        {totalWaiting > 0 && !q && (
          <div className="mt-3 flex items-center gap-2.5 rounded-xl border border-rose-400/20 bg-rose-500/10 px-3 py-2">
            <span className="relative flex w-2.5 h-2.5">
              <span className="sb-glow absolute inset-0 rounded-full bg-rose-400" />
              <span className="relative w-2.5 h-2.5 rounded-full bg-rose-500" />
            </span>
            <p className="text-xs text-rose-100">
              <b className="tabular-nums">{totalWaiting}</b> item{totalWaiting > 1 ? "s" : ""} need your action
            </p>
          </div>
        )}
      </div>

      {/* ===== Menu ===== */}
      <nav className="sb-scroll flex-1 overflow-y-auto px-3 pt-3 pb-4" aria-label="Main menu">
        {!shownSections.length && <p className="text-sm text-slate-500 text-center mt-8">{q ? `No page matches "${query}"` : "No access available"}</p>}

        {shownSections.map((section, si) => {
          const title = section.title || "";
          const A = ACCENTS[title] || ACCENTS[""];
          const hasActive = section.links.some((l) => isActivePath(l.name));
          const isClosed = !q && title && closed[title] && !hasActive;
          const sectionCount = section.links.reduce((s, l) => s + (badges[l.key] || 0), 0);
          return (
            <div key={title || `s${si}`} className={title ? "mt-4" : ""}>
              {title && (
                <button type="button" onClick={() => toggleSection(title)} className="w-full flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-lg text-[10.5px] font-bold uppercase tracking-[.14em] text-slate-500 hover:text-slate-200 transition-colors" aria-expanded={!isClosed}>
                  <span className="inline-flex items-center gap-2">
                    <span className={`w-1.5 h-1.5 rounded-full ${A.dot}`} />
                    {title}
                    {isClosed && sectionCount > 0 && <span className="text-[10px] font-bold px-1.5 rounded-full bg-rose-500 text-white normal-case tracking-normal">{sectionCount}</span>}
                  </span>
                  <FaChevronDown size={9} className={`transition-transform ${isClosed ? "-rotate-90" : ""}`} />
                </button>
              )}
              <div className={`sb-sec ${isClosed ? "sb-closed" : ""}`}>
                <div>
                  <ul className="space-y-1 mt-1">
                    {section.links.map((link) => {
                      const count = badges[link.key] || 0;
                      const on = isActivePath(link.name);
                      return (
                        <li key={link.name}>
                          <Link
                            to={`/${link.name}`}
                            onClick={handleCloseSidebar}
                            aria-current={on ? "page" : undefined}
                            className={`sb-item group relative flex items-center gap-3 h-11 pl-2 pr-2.5 rounded-xl text-[13.5px] ${on ? "text-white font-semibold" : "text-slate-300 hover:text-white hover:bg-white/[.06]"}`}
                            style={on ? { background: `linear-gradient(90deg, ${currentColor}, ${currentColor}cc)`, boxShadow: `0 10px 24px -10px ${currentColor}` } : {}}
                          >
                            {on && <span className="absolute -left-3 top-2 bottom-2 w-1 rounded-r-full bg-white" />}
                            <span className={`w-8 h-8 rounded-lg flex items-center justify-center text-[13px] shrink-0 transition-colors ${on ? "bg-white/25 text-white" : `bg-white/[.06] ${A.icon} group-hover:bg-white/10`}`}>{link.icon}</span>
                            <span className="flex-1 truncate">{link.title}</span>
                            {count > 0 && (
                              <span className={`sb-badge min-w-[22px] h-5 px-1.5 rounded-full text-[11px] font-bold flex items-center justify-center tabular-nums ${on ? "bg-white text-slate-900" : "bg-rose-500 text-white shadow shadow-rose-500/40"}`} title={`${count} waiting`}>
                                {count > 99 ? "99+" : count}
                              </span>
                            )}
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>
            </div>
          );
        })}
      </nav>

      {/* ===== Account ===== */}
      <div className="shrink-0 p-3 border-t border-white/10">
        <div className="flex items-center gap-3 rounded-2xl bg-white/[.06] border border-white/10 p-2.5">
          <span className="w-10 h-10 rounded-xl flex items-center justify-center text-white text-sm font-black shrink-0 shadow-lg" style={{ background: `linear-gradient(135deg, ${currentColor}, #6366f1)` }}>
            {initials}
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-white truncate">{String(me.name || "").trim() || "Admin"}</p>
            <p className="text-[11px] text-slate-400 inline-flex items-center gap-1">
              {isOwner ? <FaCrown size={9} className="text-amber-400" /> : <FaUserShield size={9} className="text-sky-300" />}
              {isOwner ? "Owner • full access" : `Manager • ${Math.max(0, visibleKeys.size - 1)} pages`}
            </p>
          </div>
          <button type="button" onClick={logout} title="Log out" aria-label="Log out" className="w-9 h-9 rounded-lg flex items-center justify-center text-slate-400 hover:bg-rose-500/20 hover:text-rose-300 transition-colors">
            <FaSignOutAlt size={13} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;